
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding, OnboardingProvider } from "@/context/OnboardingContext";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { ArrowLeft, LogOut } from "lucide-react";
import OnboardingProgress from "@/components/onboarding/OnboardingProgress";
import UsernameStep from "@/components/onboarding/UsernameStep";
import PhotosStep from "@/components/onboarding/PhotosStep";
import GenderStep from "@/components/onboarding/GenderStep";
import OrientationStep from "@/components/onboarding/OrientationStep";
import RoleStep from "@/components/onboarding/RoleStep";
import RelationshipTypeStep from "@/components/onboarding/RelationshipTypeStep";
import GeolocationStep from "@/components/onboarding/GeolocationStep";
import { debugLogger } from '@/utils/debugLogger';

const OnboardingSteps = () => {
  const { currentStep, goToStep, prevStep, nextStep, loading } = useOnboarding();
  const { user } = useAuth();
  const navigate = useNavigate();

  debugLogger.debug('OnboardingSteps - Component loaded, user:', !!user);

  // Verificação simples se há usuário
  useEffect(() => {
    if (!user) {
      debugLogger.debug('OnboardingSteps - No user, redirecting to auth');
      navigate("/auth");
      return;
    }
    
    debugLogger.debug('OnboardingSteps - User found, staying on onboarding page');
  }, [user, navigate]);

  if (loading) {
    debugLogger.debug('OnboardingSteps - Showing loading state');
    return (
      <div className="w-full min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-luxury-pinkBright"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  debugLogger.debug('OnboardingSteps - Rendering onboarding step:', currentStep);

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-black via-black to-gray-900">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="container max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {currentStep === 1 ? (
                <Button 
                  variant="outline" 
                  className="flex-shrink-0 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  onClick={() => navigate('/main/logout')}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair
                </Button>
              ) : (
                <>
                  <Button 
                    variant="light" 
                    className="luxury-button flex-shrink-0"
                    onClick={prevStep}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4 text-luxury-charcoal" />
                    Voltar
                  </Button>
                  
                  <Button 
                    variant="light"
                    size="icon"
                    className="flex-shrink-0 bg-white hover:bg-white/90 text-black"
                    onClick={() => navigate('/main/logout')}
                    title="Sair do onboarding"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
            
            <OnboardingProgress />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-5xl mx-auto pt-8 pb-32 px-4">
        
        {/* Username + Birthdate */}
        {currentStep === 1 && <UsernameStep />}
        
        {/* Gender */}
        {currentStep === 2 && <GenderStep />}
        
        {/* Sexual Orientation */}
        {currentStep === 3 && <OrientationStep />}
        
        {/* Role (sponsored/sponsor) */}
        {currentStep === 4 && <RoleStep />}
        
        {/* Relationship Type */}
        {currentStep === 5 && <RelationshipTypeStep />}
        
        {/* Geolocation */}
        {currentStep === 6 && <GeolocationStep />}
        
        {/* Photos - ÚLTIMA ETAPA */}
        {currentStep === 7 && <PhotosStep />}
      </div>
    </div>
  );
};

const Onboarding = () => {
  debugLogger.debug('Onboarding - Page component loaded');
  
  return (
    <OnboardingProvider>
      <OnboardingSteps />
    </OnboardingProvider>
  );
};

export default Onboarding;
